import fetchWrapper from '../fetchWrapper';

const BASE_URL = process.env.BASE_URL;

// MyRoom(마이룸) 방 채널링 진입/세션 교환용 서비스.
// enter: iframe 진입에 필요한 assertion(JWS)·iframe_base·target 을 받음.
// exchange: iframe 이 postMessage 로 보내온 code 를 MyRoom accessToken/refreshToken 으로 교환.
// status: 현재 유저의 MyRoom 연동/방 상태 조회.

interface MyRoomEnterResponse {
    // 진입 상태: ok(진입 가능) | consent_required(동의 필요) | room_unavailable(방 없음)
    //          | link_required(소셜 연결 필요 — link_url 을 열면 된다)
    //          | rejected(마이룸이 사유를 대고 거절 — 지금은 할 수 있는 게 없다)
    status?: 'ok' | 'consent_required' | 'room_unavailable' | 'link_required' | 'rejected' | string;
    // link_required 일 때 마이룸이 완성해서 주는 연결 주소(open=external 포함).
    link_url?: string;
    // rejected 일 때만. 마이룸이 준 거절 사유 코드(예: WITHDRAWN_USER_REGISTRATION)와,
    // 다시 연결할 수 있게 되는 시각(ISO8601 UTC). 시각은 사유에 따라 없을 수도 있다.
    error_code?: string;
    re_registerable_at?: string | null;
    iframe_base?: string;
    assertion?: string;
    // 마이룸 제휴 구분(운영 joysam / stage joysam-stage). iframe 해시에 partner= 로 붙인다.
    partner?: string;
    target?: string | null;
    [key: string]: any;
}

interface MyRoomExchangeResponse {
    accessToken?: string;
    refreshToken?: string;
    userId?: string;
    [key: string]: any;
}

interface MyRoomStatusResponse {
    status?: string;
    [key: string]: any;
}

// mr: 마이룸 환경 선택('test' 면 마이룸 테스트 서버). 없으면 운영.
function envQuery(mr?: string): string {
    return mr ? `?mr=${encodeURIComponent(mr)}` : '';
}

// status 전용 쿼리 — mr 에 refresh 가 더 붙을 수 있어 따로 조립한다.
function statusQuery(mr?: string, refresh?: boolean): string {
    const params = new URLSearchParams();
    if (mr) params.set('mr', mr);
    if (refresh) params.set('refresh', '1');
    const query = params.toString();
    return query ? `?${query}` : '';
}

// target: 남의 방(방구경) 진입 시 상대 partnerUserId. 없으면 내 방.
// returnUrl: 소셜 연결을 마치고 돌아올 주소. 앱은 딥링크로 돌아오므로 웹에서만 넘긴다
// (마이룸은 returnUrl 이 있으면 그쪽을 먼저 쓴다).
async function enter(target?: string, mr?: string, returnUrl?: string): Promise<MyRoomEnterResponse> {
    try {
        // _post 는 axios 전체 response 를 반환(_get 과 달리 .data 언랩 안 함).
        // BE 는 response() 로 {data:{...}} 래핑 → response.data(body).data 로 두 겹 벗긴다.
        const response = await fetchWrapper.post(`${BASE_URL}/user/myroom/enter${envQuery(mr)}`, { target, returnUrl });
        const body = response?.data;
        return body?.data ?? body;
    } catch (error) {
        throw error;
    }
}

// consent: 사용자가 동의 화면에서 "동의하고 시작하기" 를 눌렀을 때만 부른다.
// 마이룸은 서버가 임의로 동의를 보내는 것을 허용하지 않는다 — 이미 마이룸 계정이 있는
// 사람의 계정이 본인 확인 없이 조이쌤 계정에 묶이는 것을 막기 위해서다.
async function consent(target?: string, mr?: string): Promise<MyRoomEnterResponse> {
    // enter 와 같은 두 겹 언랩(_post 는 axios response 그대로, BE 는 {data:{...}} 래핑).
    const response = await fetchWrapper.post(`${BASE_URL}/user/myroom/consent${envQuery(mr)}`, { target });
    const body = response?.data;
    return (body?.data ?? body) as MyRoomEnterResponse;
}

async function exchange(code: string, mr?: string): Promise<MyRoomExchangeResponse> {
    try {
        const response = await fetchWrapper.post(`${BASE_URL}/user/myroom/exchange${envQuery(mr)}`, { code });
        const body = response?.data;
        return body?.data ?? body;
    } catch (error) {
        throw error;
    }
}

// refresh: BE 캐시(6시간)를 무시하고 마이룸에 다시 물어본다. 방을 다녀온 직후 한 번만 쓴다.
async function status(mr?: string, refresh?: boolean): Promise<MyRoomStatusResponse> {
    try {
        const response = await fetchWrapper.get(`${BASE_URL}/user/myroom/status${statusQuery(mr, refresh)}`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

// unlink: 마이룸 연동 해제(마이채널 "로그아웃"). clientSecret 이 필요한 서버 간 호출이라
// 브라우저에서 마이룸을 직접 부르지 못한다 — 조이쌤 BE 를 거친다.
async function unlink(mr?: string): Promise<{ unlinked?: boolean }> {
    const response = await fetchWrapper.post(`${BASE_URL}/user/myroom/unlink${envQuery(mr)}`, {});
    const body = response?.data;
    return body?.data ?? body;
}

// showcase: MyRoom admin 이 관리하는 샘플 방 목록 + iframe_base (신규 유저 카드/게스트 뷰용).
async function showcase(mr?: string): Promise<any> {
    try {
        const response = await fetchWrapper.get(`${BASE_URL}/user/myroom/showcase${envQuery(mr)}`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

// 화면에서만 보이는 지점을 남긴다 — 동의 화면 노출 · 연결 주소 열림 · 복귀.
//
// link_opened 가 특히 중요하다. 연결 주소를 브라우저로 넘기는 순간부터 서버 호출이
// 끊겨서, 이 호출이 없으면 "브라우저가 실제로 열렸는가" 가 양쪽 어디에도 남지 않는다
// (마이룸도 연결 페이지 방문을 기록하지 않는다 — 2026-09-18 확인).
//
// 관측용이라 실패해도 흐름을 막지 않는다. 호출자는 await 하지 않아도 된다.
// client 는 호출자가 넘긴다 — 앱인지 웹인지는 화면 쪽에서만 판정할 수 있다.
async function flowEvent(
    event: 'consent_shown' | 'link_opened' | 'link_not_left' | 'returned' | 'viewport',
    client: 'app' | 'web',
    meta?: Record<string, any>,
    mr?: string,
): Promise<void> {
    try {
        await fetchWrapper.post(`${BASE_URL}/user/myroom/flow-event${envQuery(mr)}`, {
            event,
            client,
            meta,
        });
    } catch {
        // 기록 실패가 연결을 막으면 본말전도다. 조용히 넘어간다.
    }
}

// 화면을 떠나기 직전에 남길 때 쓴다.
//
// 기록을 띄워만 놓고 바로 window.location 을 바꾸면 브라우저가 그 요청을 취소한다 —
// link_opened 가 그래서 한 건도 안 남았다(2026-09-18 stage 실측: consent_shown 은
// 남고 link_opened 만 빠졌다). axios 라 keepalive 를 못 쓰므로 보내고 기다리되,
// 기록 때문에 이동이 막히지 않게 상한을 둔다.
async function flowEventBeforeLeaving(
    event: 'link_opened',
    client: 'app' | 'web',
    meta?: Record<string, any>,
    mr?: string,
): Promise<void> {
    await Promise.race([
        flowEvent(event, client, meta, mr),
        new Promise<void>(resolve => setTimeout(resolve, 1000)),
    ]);
}

export const myRoomService = {
    enter,
    consent,
    exchange,
    unlink,
    status,
    showcase,
    flowEvent,
    flowEventBeforeLeaving,
};

export type { MyRoomEnterResponse, MyRoomExchangeResponse, MyRoomStatusResponse };
